import type { MeasuredBlock } from "@/api/document/paginate";

export interface DocumentFloatLayer {
	src: string;
	x: number;
	y: number;
	width: number;
	height: number;
}

export const DocumentBrowserLayout = {
	measureChildren(
		container: Element,
		ignoredClass?: string,
		unwrapNodeViews = false,
	): { blocks: MeasuredBlock[]; ids: string[] } {
		const children = [...container.children].filter(
			(element) => ignoredClass === undefined || !element.classList.contains(ignoredClass),
		);
		const layoutElements = children.map((element) => {
			if (!unwrapNodeViews) {
				return element;
			}

			return element.querySelector(":scope > [data-node-view-wrapper]") ?? element;
		});

		return {
			blocks: layoutElements.map((element, index) => {
				const style = window.getComputedStyle(element);
				const rect = element.getBoundingClientRect();
				const next = layoutElements.at(index + 1);

				return {
					height: rect.height,
					marginTop: Number.parseFloat(style.marginTop),
					marginBottom: Number.parseFloat(style.marginBottom),
					forcedBreak: element.classList.contains("page-break"),
					keepWithNext: next?.matches(".signature,.node-signature") ?? false,
				};
			}),
			ids: children.map((element) => (element as HTMLElement).dataset.blockId ?? ""),
		};
	},

	applyPages(pages: string[][], root: ParentNode = document) {
		const flow = root.querySelector(".doc-flow");

		if (flow === null || flow.parentElement === null) {
			throw new Error("doc_flow_not_found");
		}

		const host = flow.parentElement;
		const byId = new Map<string, Element>();

		for (const element of flow.children) {
			const id = (element as HTMLElement).dataset.blockId;

			if (id !== undefined && id !== "") {
				byId.set(id, element);
			}
		}

		for (const blockIds of pages) {
			const section = document.createElement("section");
			section.className = "doc-page";

			for (const blockId of blockIds) {
				const element = byId.get(blockId);

				if (element !== undefined) {
					section.append(element);
				}
			}

			host.append(section);
		}

		flow.remove();
	},

	applyFloats(pages: DocumentFloatLayer[][], root: ParentNode = document) {
		const sections = [...root.querySelectorAll("section.doc-page")];

		pages.forEach((paints, index) => {
			const section = sections[index] as HTMLElement | undefined;

			if (section === undefined || paints.length === 0) {
				return;
			}

			section.style.backgroundImage = paints.map((paint) => `url("${paint.src}")`).join(",");
			section.style.backgroundPosition = paints
				.map((paint) => `${paint.x}px ${paint.y}px`)
				.join(",");
			section.style.backgroundSize = paints
				.map((paint) => `${paint.width}px ${paint.height}px`)
				.join(",");
			section.style.backgroundOrigin = paints.map(() => "border-box").join(",");
			section.style.backgroundRepeat = "no-repeat";
		});
	},
};
